import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/api/client";

// Wire-format mirrors the projects routes in
// src/flowstack/server/routes/projects.py (B1 PR landed the FastAPI
// readers). Status union widens to the canonical LifecycleStateLabel
// from src/flowstack/lifecycle/state_machine.py — the GUI never
// computes a state; backend is authoritative.

/** Canonical run lifecycle labels (mirrors
 * src/flowstack/lifecycle/state_machine.LifecycleStateLabel). */
export type LifecycleStateLabel =
  | "validating"
  | "cached"
  | "staging_inputs"
  | "dispatched"
  | "running"
  | "collecting_outputs"
  | "validating_outputs"
  | "completed"
  | "failed_validation"
  | "failed_system"
  | "failed_contract"
  | "failed_scientific";

export interface Project {
  project_id: string;
  tenant_id: string;
  slug: string;
  display_name: string;
  description: string | null;
  created_at: string;
  // Aggregate counts the projects-list page shows without a second
  // fetch. Backend computes these once at query time.
  run_count: number;
  active_run_count: number;
  artifact_count: number;
}

export interface ProjectListResponse {
  projects: Project[];
}

// Run rollup that the detail page needs alongside the project header.
// Same wire shape the backend's /api/projects/<slug>/runs endpoint
// emits — keys mirror the catalog's run-lifecycle row shape.
//
// ``project_slug`` is optional because the project-scoped endpoint
// (/api/projects/<slug>/runs) omits it (the consumer already knows
// which project the row belongs to); the global /api/runs endpoint
// emits it on every row so the global run list can render a Project
// column.
export interface ProjectRun {
  run_id: string;
  flow_name: string | null; // null for single-Tile dispatches
  tile_name: string | null; // null for Flow runs
  status: LifecycleStateLabel;
  submitted_at: string;
  // The compute window VCM recorded for this run, on VCM's clock
  // (flowstack#1270). `null` means **not recorded** and NOTHING else: a row
  // written before the columns existed (no backfill — nothing was captured to
  // backfill from), a run FlowStack terminalised itself, a stub-mode run, or a
  // run still in flight.
  //
  // It is never evidence ABOUT the run — not a cache hit, not a fast run, not
  // a failure. An investigation on this project already read `started_at:
  // null` as "served from cache" across 300 rows of which zero were
  // populated. Render absence through `components/runs/run-timing.ts`, which
  // says "not recorded" rather than leaving a blank that reads as zero.
  started_at: string | null;
  finished_at: string | null;
  submitted_by: string;
  project_slug?: string;
}

export interface ProjectRunsResponse {
  runs: ProjectRun[];
}

/** The prefix every project query key shares — list, detail and the
 * project-scoped run feed. Invalidate THIS to drop all of them at once. */
export const PROJECTS_ROOT_QUERY_KEY = ["projects"] as const;

export const PROJECTS_QUERY_KEY = [...PROJECTS_ROOT_QUERY_KEY, "list"] as const;

export function projectQueryKey(slug: string): readonly unknown[] {
  return [...PROJECTS_ROOT_QUERY_KEY, "detail", slug] as const;
}

export function projectRunsQueryKey(slug: string): readonly unknown[] {
  return [...PROJECTS_ROOT_QUERY_KEY, "runs", slug] as const;
}

export function useProjects() {
  return useQuery({
    queryKey: PROJECTS_QUERY_KEY,
    queryFn: async (): Promise<Project[]> => {
      const response = await apiFetch<ProjectListResponse>("/api/projects");
      return response.projects;
    },
    staleTime: 30_000,
  });
}

export function useProject(slug: string) {
  return useQuery({
    queryKey: projectQueryKey(slug),
    queryFn: async (): Promise<Project> => {
      return apiFetch<Project>(`/api/projects/${encodeURIComponent(slug)}`);
    },
    staleTime: 30_000,
    enabled: Boolean(slug),
  });
}

export function useProjectRuns(slug: string) {
  return useQuery({
    queryKey: projectRunsQueryKey(slug),
    queryFn: async (): Promise<ProjectRun[]> => {
      const response = await apiFetch<ProjectRunsResponse>(
        `/api/projects/${encodeURIComponent(slug)}/runs`
      );
      return response.runs;
    },
    // Runs change more often than the project header — short stale.
    staleTime: 10_000,
    enabled: Boolean(slug),
  });
}
